import { Component, OnInit, DestroyRef, inject, signal, ViewChild, AfterViewInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { distinctUntilChanged, firstValueFrom } from "rxjs";
import { finalize } from "rxjs/operators";

// Services
import { MsalService } from "@azure/msal-angular";
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { GetSectionGroupService } from "../../../data/section-group/get-section-group.service";
import { UpdateSectionGroupService } from "../../../data/section-group/update-section-group.service";
import { CreateSectionGroupService } from "../../../data/section-group/create-section-group.service";
import { CreateSectionGroupAdminService } from "../../../data/section-group/create-section-group-admin.service";

import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import {
  getPreview,
  formatErrors,
} from "../user-data-template-details/user-data-template-details.component";
import { ListSectionGroupAdminsService } from "../../../data/section-group/list-section-group-admins.service";
import {
  SectionGroupAdminDetailsComponent,
  SectionGroupAdminDetailMode,
} from "../../admin-section-group/section-group-admin-details/section-group-admin-details.component";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { SECTION_GROUP_DETAILS_I18N } from "./section-group-details.i18n";
import { PRICING_MODEL, BILLING_FREQUENCY, SECTION_GROUP_CONTRACT_CHANNEL, SECTION_BILL_TO_TYPE } from "src/app/constants/app-constants";
import { Addresses, AddressesFormComponent, AddressesDefaultValue } from "../../shared/addresses-form/addresses-form.component";
import { ContactDetails, ContactDetailsFormComponent, ContactDetailsDefaultValue } from "../../shared/contact-details-form/contact-details-form.component";
import { SgBillingInfo, SgBillingInfoFormComponent, SgBillingInfoDefaultValue } from "../../shared/billing-info-form/section-group-billing-info-form.component";
import { GetDistributorService } from "../../../data/distributor/get-distributor.service";
import { DistributorDocument } from "../distributor-details/distributor-details.component";
import { DebugUtils } from "../../../shared/utils/debug-utils";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ListDistributorsService } from "src/app/data/distributor/list-distributors.service";

@Component({
  selector: "app-section-details",
  templateUrl: "./section-group-details.component.html",
  styleUrls: ["./section-group-details.component.css"],
  imports: [
    SHARED_IMPORTS,
    AddressesFormComponent,
    ContactDetailsFormComponent,
    SgBillingInfoFormComponent
  ]
})
export class SectionGroupDetailsComponent implements OnInit, AfterViewInit {
  @ViewChild('billingInfoFormComp') billingInfoFormComponent!: SgBillingInfoFormComponent;

  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = SECTION_GROUP_DETAILS_I18N;

  public sectionGroupData: SectionGroupDocument;

  public mode = Mode.disp;
  Mode = Mode;

  public pricingModel = PRICING_MODEL;
  public billingFrequency = BILLING_FREQUENCY;
  public sectionGroupContractChannel = SECTION_GROUP_CONTRACT_CHANNEL;
  public sectionBillToType = SECTION_BILL_TO_TYPE;

  returnToUrl;
  homeUrl;

  errors;
  previews;

  userDataTemplates;
  userDataTemplatesFiltered;
  canUpdate;
  hasSectionGroupAdminManagement = false;
  hasAllSectionGroupManagement = false;
  hasDistributorAgent = false;
  // distributor : DistributorDocument | undefined | null;
  distributors : DistributorDocument[] | undefined | null;

  admins = signal<any[]>([]);
  public billToInfos = signal<any[]>([]);

  fromSystemAdmin; //アドレスがsystem-adminかsection-group-adminかの判定をするために使用

  changesMade = false;
  isSending = signal(false);

  sectionGroupFormGroup;

  shouldWarnOnUserDataAttributesChange : boolean = true;

  private readonly defaultSectionGroupData: SectionGroupDocument = {
    id: null, // 新規作成時は null 
    type: "SectionGroup", // 必要に応じてC#側のドキュメントタイプ（固定値など）を指定
    name: "",

    // すでに定義されている既定値オブジェクトをそのまま割り当て
    addresses: AddressesDefaultValue,
    contactDetails: ContactDetailsDefaultValue,

    billingInfo: SgBillingInfoDefaultValue,

    commonInfo: {
      lastModifiedAt: "",
      lastModifiedBy: "",
    },

    settings: {
      language: "ja", // デフォルトの言語（必要に応じて変更してください）
      monthlyQuotaGB: 10,
      userDataAttributes: "",
      functionAttributes: [], // 送信直前に動的に付与するが、初期値として空配列を置いておく
      assets: {
        logoUrl: "",
        additional: ""
      }
    },

    admins: [],

  };

  contractStatus = [
    { value: "OPEN", viewValue: SECTION_GROUP_DETAILS_I18N.contractStatus.active },
    { value: "CLOSE", viewValue: SECTION_GROUP_DETAILS_I18N.contractStatus.inactive },
  ];

  assignAdmin = [
    { value: false, desc: SECTION_GROUP_DETAILS_I18N.assignAdmin.later },
    { value: true, desc: SECTION_GROUP_DETAILS_I18N.assignAdmin.self },
  ];

  sectionGroupId: any;

  constructor(
    public dialogRef: MatDialogRef<SectionGroupDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private msalService: MsalService,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
    public common: DatabaseCacheService,
    private getSectionGroupService: GetSectionGroupService,
    private updateSectionGroupService: UpdateSectionGroupService,
    private createSectionGroupService: CreateSectionGroupService,
    private createSectionGroupAdminService: CreateSectionGroupAdminService,
    private listSectionGroupAdminsService: ListSectionGroupAdminsService,
    private getDistributorService: GetDistributorService,
    public listDistributorsService: ListDistributorsService,
    private router: Router,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.listDistributors();

    // 新規作成・更新が終わったあとで戻るURL
    this.returnToUrl = this.router.url;
    const secondSlashIndex = this.returnToUrl.indexOf("/", 1); // 最初のスラッシュ以降を検索
    this.homeUrl =
      secondSlashIndex !== -1
        ? this.returnToUrl.substring(0, secondSlashIndex)
        : this.returnToUrl;

    this.userDataTemplates = this.data?.userDataTemplates;
    this.userDataTemplatesFiltered = this.userDataTemplates; 

    this.sectionGroupData = this.data?.sectionGroupData ?? this.defaultSectionGroupData;
    this.sectionGroupId = this.sectionGroupData.id;

    this.sectionGroupFormGroup = new FormGroup({
      assignAdmin: new FormControl(""), // 表示専用。バックエンドには送らない
      allFunctions: new FormControl(""), // 表示専用。バックエンドには送らない

      id: new FormControl(""),

      name: new FormControl("", [Validators.required]),

      addresses: AddressesFormComponent.createAddressesGroup(),
      contactDetails: ContactDetailsFormComponent.createContactDetailsGroup(),

      billingInfo: SgBillingInfoFormComponent.createSgBillingInfoGroup(),

      commonInfo: new FormGroup({
        lastModifiedAt: new FormControl(""),
        lastModifiedBy: new FormControl("")
      }),

      settings: new FormGroup({
        language: new FormControl(""), 
        userDataAttributes: new FormControl("", [Validators.required]),
        functionAttributes: new FormGroup({}),
        monthlyQuotaGB: new FormControl(10),
        assets: new FormGroup({
          logoUrl: new FormControl(""),
          additional: new FormControl("")
        })
      }),

    });

    this.setChangeEvents(); // patchValueのときに呼ばれてほしいので、それより前に changeEventを割り当てておく

    // functionAttributesの要素はときどき変化するため動的に追加するようにする
    const functionAttributesGroup = this.sectionGroupFormGroup.get('settings.functionAttributes') as FormGroup;
    if (functionAttributesGroup) {
      this.common.SectionGroupFunctions.forEach((f) =>
        functionAttributesGroup.addControl(f.code, new FormControl(false))
      );
    }

    this.sectionGroupFormGroup.disable();

    // 新規作成のとき、代理店のエージェントであれば契約チャネルを代理店にする。これはpatchValueより前にやっておかないと辻褄が合わなくなる
    if (this.sectionGroupData.id == null && this.common.hasAnyDistributorAgentRole()){
        var distributorId = this.common.getDistributorAgents()[0].distributorId 

        this.sectionGroupData.billingInfo.contractChannel = "DISTRIBUTOR";
        this.sectionGroupData.billingInfo.billToDistributorId = distributorId;

        this.onContractChannelSelect("DISTROBUTOR"); // ユーザデータテンプレートをリフレッシュする
        this.shouldWarnOnUserDataAttributesChange = false; // 新規作成のときはユーザデータのところを変更してもなにも問題は無いので警告をオフに
    }

    // // 代理店経由のときは代理店情報を取得しておく
    // if (this.sectionGroupData.billingInfo?.contractChannel == "DISTRIBUTOR"){
    //   this.getDistributorAsync(this.sectionGroupData.billingInfo.billToDistributorId);
    // }

    this.sectionGroupFormGroup.patchValue(this.sectionGroupData);

    console.log("========================  sectionGroupFormGroup after patchValue");
    DebugUtils.dumpFormStructure(this.sectionGroupFormGroup);
    console.log("========================  sectionGroupFormGroup after patchValue");

    if (this.common.hasAllSectionGroupManagement()) {
      this.sectionGroupFormGroup.get("assignAdmin")?.setValue(false);
    } else {
      // 現在のユーザーが All SectoinGroup Managementを持っていないときは現在のユーザーが管理者になる前提（管理者にならないように指定しなおすこともできるが、自分自身が管理できなくなるので多分めんどくさくなる）
      this.sectionGroupFormGroup.get("assignAdmin")?.setValue(true);
    }

    // すでにオンに設定されている「利用可能な機能」だけチェックを入れておく
    this.sectionGroupData.settings.functionAttributes.forEach((f) => {
      this.sectionGroupFormGroup.get(`settings.functionAttributes.${f}`)?.setValue(true, { emitEvent: false });
    });

    this.refreshAllFunctionsCheckbox();
    this.refreshPreviewAndErrors();

    console.log("========================  sectionGroupFormGroup after initialize");
    DebugUtils.dumpFormStructure(this.sectionGroupFormGroup);
    console.log("========================  sectionGroupFormGroup after initialize");

    this.permissionCheck();
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.hasAllSectionGroupManagement =  this.common.hasAllSectionGroupManagement();
        this.hasDistributorAgent = this.common.hasAnyDistributorAgentRole();
        this.permissionCheck();
      });

    // システム管理者かセクショングループ管理者かの判定
    this.fromSystemAdmin = this.router.url.includes("system-admin");
  }

  ngAfterViewInit(): void {    
    // 新規作成のときは入力モードにする
    // ngOnInit()から呼ぶと、ViewChildで参照している billingInfoFormComponent が初期化されていないので期待したとおりに実行されない
    if (this.sectionGroupData.id == null) {
      this.enableInput();
      this.mode = Mode.create;
    } else {
      this.listAdmins();
    }
  }

  listDistributors(){
    this.listDistributorsService.listAdministeredDistributors()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          console.log("distributors list res=", res);
          this.distributors = res as DistributorDocument[];
          console.log("distributors list this.distributors=", this.distributors);

          this.billingInfoFormComponent?.receiveDistributorsFromParentForm(this.distributors);
        },
        error: (error) => {
          console.error(error);
        }
      });
  }
    
  setChangeEvents() {
    // contractChannelの変更があるといくつかのフィールドの表示非表示が変わるが、そちらは SgBillingInfoForm 内でハンドルする
    // ここでは ユーザデータテンプレートのドロップダウンだけをリフレッシュする
    const contractChannel = this.sectionGroupFormGroup.get('billingInfo.contractChannel');
  
    if (contractChannel) {
      contractChannel.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef)) // 💡コンポーネント破棄時に自動クリーンアップ
        .subscribe((value: string) => {
          // 初期値の代入時にも実行される
          // ユーザーがクリックして変更した時も、ここが走る
          this.onContractChannelSelect(value);
        });
    }
    
    const distributorIdControl = this.sectionGroupFormGroup.get('billingInfo.billToDistributorId');
  
    if (distributorIdControl) {
      distributorIdControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef)) // 💡コンポーネント破棄時に自動クリーンアップ
        .subscribe((value: string) => {
          // 初期値の代入時にも実行される
          // ユーザーがクリックして変更した時も、ここが走る
          this.onDistributorSelect(value);
        });
    }
  }

  private onContractChannelSelect(value: string){
    if (value == "DIRECT"){
      this.userDataTemplatesFiltered = this.userDataTemplates;
    } 
    else{
      let distributorId = this.sectionGroupFormGroup.get('billingInfo.billToDistributorId')?.value;
      if (distributorId){
        this.onDistributorSelect(distributorId);
      }
      else{
        this.userDataTemplatesFiltered = [];
      }
    }
  }

  private onDistributorSelect(distributorId : string) {
    console.log("onDistributorSelecte", this.distributors, distributorId);
    let distributor = this.distributors?.find(d => d.id == distributorId);

    if (distributor){
      this.userDataTemplatesFiltered = this.userDataTemplates.filter(u => distributor?.userDataTemplateChoices.includes(u.id));
    }
  }

  permissionCheck() {
    this.canUpdate = this.common.hasAllSectionGroupManagement();

    this.canUpdate ||= this.common.hasSectionGroupCreate();
    this.canUpdate ||= this.common.hasAnyDistributorAgentRole();
    if (this.sectionGroupId != null) {
      this.canUpdate ||= this.common.hasSectionGroupUpdate(this.sectionGroupId);

      this.hasSectionGroupAdminManagement = this.common.hasSectionGroupAdminManagement(this.sectionGroupId);
    }
  }

  onUserDataTemplateSelected(event: Event): void {
    if (this.shouldWarnOnUserDataAttributesChange){
      var userDataAtributesChangedDialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: false,
        data: {
          title: this.i18n.userDataAttributesChangedDialog.confirmTitle,
          message: this.i18n.userDataAttributesChangedDialog.confirmMessage,
        },
      });

      userDataAtributesChangedDialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {

        if (!result) {
          this.snackBar.open(this.i18n.message.userDataAttributesChangeCanceled , this.i18n.button.close, { duration: 5000 });
          return;
        }

        this.applyUserDataTemplateSelected(event);
      });

      return;
    }

    this.applyUserDataTemplateSelected(event);
  }

  private applyUserDataTemplateSelected(event: Event){
    this.shouldWarnOnUserDataAttributesChange = false;

    const dropDownValue = event.target as HTMLSelectElement;
    const selectedTemplate = this.userDataTemplates.find(
      (u) => u.id == dropDownValue.value,
    );

    this.sectionGroupFormGroup.get("settings.userDataAttributes").setValue(JSON.stringify(selectedTemplate.template));

    this.refreshPreviewAndErrors();
  }

  onUserDataAttributesChanged(event: Event): void{
    if (this.shouldWarnOnUserDataAttributesChange){
      var userDataAtributesChangedDialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: false,
        data: {
          title: this.i18n.userDataAttributesChangedDialog.confirmTitle,
          message: this.i18n.userDataAttributesChangedDialog.confirmMessage,
        },
      });

      userDataAtributesChangedDialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {

        if (!result) {
          // 元の値に戻す
          this.sectionGroupFormGroup.get("settings.userDataAttributes").setValue(this.sectionGroupData.settings.userDataAttributes);
          this.snackBar.open(this.i18n.message.userDataAttributesChangeCanceled , this.i18n.button.close, { duration: 5000 });
          return;
        }
        this.refreshPreviewAndErrors();
        this.shouldWarnOnUserDataAttributesChange = false;
      });

      return;
    }

    this.refreshPreviewAndErrors();
  }


  onNoClick(): void {
    this.dialogRef.close();
  }

  switchToEditMode() {
    this.mode = Mode.edit;

    this.enableInput();
  }

  enableInput() {

    this.sectionGroupFormGroup.get("assignAdmin").enable();

    this.sectionGroupFormGroup.get("name").enable();

    this.sectionGroupFormGroup.get("addresses").enable();
    this.sectionGroupFormGroup.get("contactDetails").enable();

    if (this.common.hasAllSectionGroupManagement()){
      // 以下は契約に関わるのでセクショングループ管理者のレベルでは変更できない
      this.sectionGroupFormGroup.get("billingInfo").enable();
 
      this.sectionGroupFormGroup.get("settings.assets.logoUrl").enable();
      this.sectionGroupFormGroup.get("settings.userDataAttributes").enable();

      this.sectionGroupFormGroup.get("allFunctions").enable();
      this.sectionGroupFormGroup.get(`settings.functionAttributes`)?.enable();

      // 下位のフォームグループの 値変更イベントハンドラ を呼び出すことで、連鎖的に下位のグループの項目の enable / disable を行なう
      this.onContractChannelSelect(this.sectionGroupFormGroup.get("billingInfo.contractChannel")?.value);
      this.billingInfoFormComponent?.onContractChannelChange(this.sectionGroupFormGroup.get("billingInfo.contractChannel")?.value);
    }

    if (this.common.hasAllSectionGroupManagement() || this.common.hasQuotaManagement()) {
      this.sectionGroupFormGroup.get("settings.monthlyQuotaGB").enable();
    }


    console.log("--- フォーム階層構造 Enable後 ---");
    DebugUtils.dumpFormStructure(this.sectionGroupFormGroup);
    console.log("----------------------------------");


    this.sectionGroupFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.changesMade = true;
      });

    return;
  }



  listAdmins() {
    this.listSectionGroupAdminsService
      .listSectionGroupAdmins(this.sectionGroupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.admins.set(res as any[]);
        },
        (error) => {
        },
      );
  }



  refreshPreviewAndErrors() {
    const userDataAttributesString =
      this.sectionGroupFormGroup.get("settings.userDataAttributes").value;
    let errors = formatErrors(userDataAttributesString);
    if (errors.length == 0) {
      this.errors = ["エラーはありません"];
    } else {
      this.errors = errors;
    }

    try {
      const userDataAttributes = JSON.parse(userDataAttributesString);
      this.previews = getPreview(userDataAttributes);
    } catch (e) {
      this.previews = [this.i18n.validation.jsonParseError];
    }
  }

  
  refreshAllFunctionsCheckbox() {
    let allFunctions = true;
    let noFunctions = true;
    
    // 新しい階層から値を取得
    const functionValues = this.sectionGroupFormGroup.get('settings.functionAttributes')?.value || {};

    this.common.SectionGroupFunctions.forEach((f) => {
      if (functionValues[f.code]) {
        noFunctions = false;
      } else {
        allFunctions = false;
      }
    });

    const ctrl = this.sectionGroupFormGroup.get('allFunctions');
    if (allFunctions) {
      ctrl?.setValue(true, { emitEvent: false });
    } else if (noFunctions) {
      ctrl?.setValue(false, { emitEvent: false });
    } else {
      ctrl?.setValue(false, { emitEvent: false });
    }
  }

  // allFunctionsCheckboxClicked() {
  //   const check = !!this.sectionGroupFormGroup.get('allFunctions')?.value;
  //   this.common.SectionGroupFunctions.forEach((f) => {
  //     this.sectionGroupFormGroup.get(f.code)?.setValue(check);
  //   });
  // }

  allFunctionsCheckboxClicked() {
    const check = !!this.sectionGroupFormGroup.get('allFunctions')?.value;
    this.common.SectionGroupFunctions.forEach((f) => {
      this.sectionGroupFormGroup.get(`settings.functionAttributes.${f.code}`)?.setValue(check);
    });
  }

  onEditOrUploadClicked() {
    // 現在がディスプレイモードなら編集モードに切り替える
    if (this.mode == Mode.disp) {
      this.switchToEditMode();
      return;
    }

    //　各フィールドにValidationRuleを適用し確認
    this.sectionGroupFormGroup.markAllAsTouched();
    // this.sectionGroupFormGroup.updateValueAndValidity();
    if (!this.sectionGroupFormGroup.valid) {
      console.warn('--- フォームのバリデーションエラー一覧 ---');
      DebugUtils.logFormErrors(this.sectionGroupFormGroup);
      console.warn('---------------------------------------');

      let snack = this.snackBar.open(
        this.i18n.validation.checkFields,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );
      return;
    }

    //そうでない場合はすでに編集モードなのでデータベースにアップロードする
    // let userDataAttributesString = this.sectionGroupFormGroup.value.userDataAttributes;
    let userDataAttributesString = this.sectionGroupFormGroup.get('settings.userDataAttributes')?.value;
    let errors = formatErrors(userDataAttributesString);
    if (errors.length > 0) {
      let snack = this.snackBar.open(
        this.i18n.validation.userDataFormatError,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );

      return;
    }

    var postBody = this.sectionGroupFormGroup.getRawValue();

    console.log("========================  sectionGroupFormGroup before submit");
    DebugUtils.dumpFormStructure(this.sectionGroupFormGroup);
    console.log("========================  sectionGroupFormGroup before submit");

    postBody.settings.userDataAttributes = JSON.parse(userDataAttributesString);

    if (postBody.billingInfo.contractChannel == 'DISTRIBUTOR' )
    {
      let distributor = this.distributors?.find(d => d.id == this.sectionGroupFormGroup.get('billingInfo.billToDistributorId')?.value);
      postBody.settings.functionAttributes = distributor?.functionAttributes;
    }
    else{
      postBody.settings.functionAttributes = [];

      let noFunctionsSelected = true;

      // 2. フォームの settings.functionAttributes グループから、各関数の true/false 状態を一括取得
      const functionValues = this.sectionGroupFormGroup.get('settings.functionAttributes')?.value || {};

      // 3. ループを回して、値が true になっている関数のコードだけを配列にプッシュ
      this.common.SectionGroupFunctions.forEach((f) => {
        if (functionValues[f.code]) {
          postBody.settings.functionAttributes.push(f.code);
          noFunctionsSelected = false;
        }
      });
      if (noFunctionsSelected) {
        let snack = this.snackBar.open(
          this.i18n.validation.selectAtLeastOneFunction,
          this.i18n.button.close,
          {
            duration: 30000,
          },
        );

        return;
      }
    }


    console.log("sectionGroup postBody", postBody);

    this.isSending.set(true);
    if (this.sectionGroupId == null) {
      this.createSectionGroup(postBody);
    } else {
      this.updateSectionGroup(postBody);
    }
  }



  createSectionGroup(postBody) {
    this.createSectionGroupService
      .createSectionGroup(postBody)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!postBody.assignAdmin) {
            this.isSending.set(false);
            this.sectionGroupCreated(this.i18n.message.createSuccess);
            return;
          }

          let user = this.msalService.instance.getActiveAccount();
          let loginUserId = user.idTokenClaims["sub"];

          let newSectionGroupId = (res as any).id;

          let postBodyCreateAdmin: Record<string, any> = {};
          postBodyCreateAdmin.createUser = false;
          postBodyCreateAdmin.sectionGroupId = newSectionGroupId;
          postBodyCreateAdmin.userId = loginUserId;
          postBodyCreateAdmin.roles = this.common.sectionGroupAdminRoles.map((r) => r.code);

          postBody.sectionGroupId = this.common.getSelectedSectionGroup()?.id;

          this.createSectionGroupAdminService
            .createSectionGroupAdmin(newSectionGroupId, postBodyCreateAdmin)
            .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
            .subscribe({
              next: (res) => {
                this.sectionGroupCreated(this.i18n.message.createWithAdminSuccess);
              },
              error: (e) => {
                this.dialogRef.close({ needRefresh: true });
                this.snackBar.open(
                  `${this.i18n.message.createAdminError}${JSON.stringify(e.error)}`,
                  this.i18n.button.close,
                  { duration: 10000 },
                );
              },
            });
        },
        error: (e) => {
          this.isSending.set(false);
          this.snackBar.open(
            `${this.i18n.message.createError}${JSON.stringify(e.error)}`,
            this.i18n.button.close,
            { duration: 10000 },
          );
        },
      });
  }

  sectionGroupCreated(message) {
    this.dialogRef.close({ needRefresh: true });
    this.snackBar.open(message, this.i18n.button.close, { duration: 5000 });
  }

  updateSectionGroup(postParams) {
    this.updateSectionGroupService
      .updateSectionGroup(this.sectionGroupId, postParams)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe({
        next: (res) => {
          this.dialogRef.close({ needRefresh: true });
          this.snackBar.open(
            this.i18n.message.updateSuccess,
            this.i18n.button.close,
            { duration: 5000 },
          );
        },
        error: (e) => {
          this.snackBar.open(
            this.i18n.message.updateError,
            this.i18n.button.close,
            { duration: 10000 },
          );
        },
      });
  }

  openSectionGroupAdmin() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        "/section-group-admin/" + this.sectionGroupId + "/home",
      ]),
    );
    window.open("#" + url, "_blank");
  }

  inviteAdmin() {
    const dialogRef = this.dialog.open(SectionGroupAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sectionGroupId: this.sectionGroupId,
        mode: SectionGroupAdminDetailMode.invite,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {});
  }

  createAdmin() {
    const dialogRef = this.dialog.open(SectionGroupAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sectionGroupId: this.sectionGroupId,
        mode: SectionGroupAdminDetailMode.create,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.needRefresh) {
          this.listAdmins();
        }
      });
  }



  getAddressesGroup(): FormGroup {
    return this.sectionGroupFormGroup.get('addresses') as FormGroup;
  }

  getContactDetailsGroup(): FormGroup {
    return this.sectionGroupFormGroup.get('contactDetails') as FormGroup;
  }

  // getBillingTermsGroup(): FormGroup {
  //   return this.sectionGroupFormGroup.get('billingInfo.billingTerms') as FormGroup;
  // }

  // getBillingSourceGroup(): FormGroup {
  //   return this.sectionGroupFormGroup.get('billingInfo.billingSource') as FormGroup;
  // }

  getBillingInfoGroup(): FormGroup {
    return this.sectionGroupFormGroup.get('billingInfo') as FormGroup;
  }

  allFunctionsControl(): FormControl {
    return this.sectionGroupFormGroup.get('allFunctions') as FormControl;
  }
}

export enum Mode {
  disp,
  create,
  edit,
}

export interface SectionGroupSettings {
  language: string;
  monthlyQuotaGB: number;
  userDataAttributes: string;
  functionAttributes?: any; // 送信直前に付与する動的な値
  assets: SectionGroupAssets;
}

export interface SectionGroupAssets {
  logoUrl: string;
  additional: string;
}

// export interface SectionGroupBillingInfo {
//   contractChannel: SectionGroupContractChannel; // Enum (文字列)
//   billToDistributorId?: string;                 // 代理店ID（オプション）
//   BillingInfo: BillingTerms;
//   billingSource: BillingSource;
// }

export interface SectionGroupDocument {
  id: string | null;
  type?: string;
  name: string;
  addresses: Addresses;
  contactDetails: ContactDetails;
  billingInfo: SgBillingInfo;
  commonInfo: {
    lastModifiedAt: string;
    lastModifiedBy: string;
  };
  settings: SectionGroupSettings;

  admins: [], // 表示専用
}


// ※ Addresses, ContactDetails などの共通パーツもインターフェース化しておくと便利です
